import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from './SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiSettings, FiSave, FiCheck, FiAlertTriangle, FiShield, FiKey } = FiIcons;

const AdvancedSettingsPanel = ({ isOpen, onClose }) => {
  const [selectedModel, setSelectedModel] = useState('gpt-5.5');
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [selectedDetail, setSelectedDetail] = useState('standard');
  const [apiKey, setApiKey] = useState('');
  const [keyStatus, setKeyStatus] = useState('none');

  const availableModels = [
    { value: 'gpt-5.5', label: 'GPT-5.5 - Recommended', description: 'Latest flagship, multimodal' },
    { value: 'gpt-5.4', label: 'GPT-5.4', description: 'Strong quality, lower cost' },
    { value: 'gpt-5.4-mini', label: 'GPT-5.4 Mini', description: 'Fast and economical' }
  ];

  const providers = [
    { value: 'openai', label: 'OpenAI (Official)', description: 'Official API' }
  ];

  const detailLevels = [
    { value: 'standard', label: 'Standard', description: 'Balanced analysis' },
    { value: 'detailed', label: 'Detailed', description: 'More information' },
    { value: 'professional', label: 'Professional', description: 'Institutional analysis' }
  ];

  useEffect(() => {
    if (isOpen) {
      const savedModel = localStorage.getItem('snapchart_openai_model') || localStorage.getItem('atlas_openai_model') || 'gpt-5.5';
      const savedProvider = localStorage.getItem('snapchart_api_provider') || 'openai';
      const savedDetail = localStorage.getItem('snapchart_detail_level') || 'standard';
      const savedApiKey = localStorage.getItem('snapchart_openai_api_key') || 
                          localStorage.getItem('atlas_openai_api_key') || 
                          localStorage.getItem('openai_api_key') || '';

      setSelectedModel(savedModel);
      setSelectedProvider(savedProvider);
      setSelectedDetail(savedDetail);
      setApiKey(savedApiKey);
      validateApiKey(savedApiKey);
    }
  }, [isOpen]);

  const validateApiKey = (key) => {
    if (!key || key.trim() === '') {
      setKeyStatus('none');
    } else if (!key.startsWith('sk-')) {
      setKeyStatus('invalid');
    } else if (key.length < 40) {
      setKeyStatus('invalid');
    } else {
      setKeyStatus('valid');
    }
  };

  const handleApiKeyChange = (e) => {
    const newKey = e.target.value;
    setApiKey(newKey);
    validateApiKey(newKey);
  };

  const handleSave = () => {
    localStorage.setItem('snapchart_openai_model', selectedModel);
    localStorage.setItem('snapchart_api_provider', selectedProvider);
    localStorage.setItem('snapchart_detail_level', selectedDetail);
    
    if (apiKey && apiKey.trim() !== '' && keyStatus === 'valid') {
      localStorage.setItem('snapchart_openai_api_key', apiKey.trim());
      localStorage.setItem('atlas_openai_api_key', apiKey.trim());
    }

    console.log('✅ Settings saved:', {
      model: selectedModel,
      provider: selectedProvider,
      detail: selectedDetail,
      hasApiKey: keyStatus === 'valid'
    });

    onClose();
  };

  const getKeyStatusDisplay = () => {
    switch (keyStatus) {
      case 'valid':
        return (
          <div className="flex items-center text-green-400 text-sm mt-1">
            <SafeIcon icon={FiCheck} className="mr-1" />
            Valid API key (used as fallback)
          </div>
        );
      case 'invalid':
        return (
          <div className="flex items-center text-red-400 text-sm mt-1">
            <SafeIcon icon={FiAlertTriangle} className="mr-1" />
            Invalid key format
          </div>
        );
      case 'none':
      default:
        return (
          <div className="flex items-center text-gray-400 text-sm mt-1">
            <SafeIcon icon={FiShield} className="mr-1" />
            Server key used by default
          </div>
        );
    }
  };

  const getSelectedModelInfo = () => {
    const modelInfo = availableModels.find(m => m.value === selectedModel);
    if (!modelInfo) return null;

    return (
      <div className="mt-2 p-3 bg-gray-900 rounded-lg border border-gray-700">
        <div className="text-sm text-gray-300"><strong>{modelInfo.label}</strong></div>
        <div className="text-xs text-gray-400 mt-1">{modelInfo.description}</div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold flex items-center">
            <SafeIcon icon={FiSettings} className="mr-2 text-blue-400" />
            Advanced Settings
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-6">
          {/* API Status */}
          <div className="bg-green-900/20 border border-green-700/50 rounded-lg p-4">
            <div className="flex items-center text-green-400 mb-2">
              <SafeIcon icon={FiShield} className="mr-2" />
              <span className="font-medium">OpenAI API Active</span>
            </div>
            <div className="text-sm text-green-300/80">
              The API automatically uses the secure server. You can add your own key as fallback if needed.
            </div>
          </div>

          {/* API Key Fallback */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <SafeIcon icon={FiKey} className="inline mr-1" />
              OpenAI API Key (Optional - Fallback)
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={handleApiKeyChange}
              placeholder="sk-... (optionnel)"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
            {getKeyStatusDisplay()}
            <div className="text-xs text-gray-400 mt-2">
              Used automatically if the server doesn't respond. 
              <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline ml-1">
                Get a key
              </a>
            </div>
          </div>

          {/* AI Model */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">AI Model</label>
            <select 
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:border-blue-500"
            >
              {availableModels.map(model => (
                <option key={model.value} value={model.value}>{model.label}</option>
              ))}
            </select>
            {getSelectedModelInfo()}
          </div>
          
          {/* Detail Level */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Detail Level</label>
            <select 
              value={selectedDetail}
              onChange={(e) => setSelectedDetail(e.target.value)}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:border-blue-500"
            >
              {detailLevels.map(level => (
                <option key={level.value} value={level.value}>{level.label}</option>
              ))}
            </select>
            <div className="text-xs text-gray-400 mt-1">
              {detailLevels.find(l => l.value === selectedDetail)?.description}
            </div>
          </div>

          {/* Info */}
          <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
            <div className="text-sm text-blue-300 font-medium mb-2">💡 How it works</div>
            <div className="text-xs text-blue-200 space-y-1">
              <div>• The system first tries the secure server</div>
              <div>• If the server doesn't respond, your personal key is used</div>
              <div>• Your key remains 100% private (stored locally)</div>
            </div>
          </div>
          
          {/* Buttons */}
          <div className="flex space-x-3 mt-6">
            <button
              onClick={handleSave}
              className="flex-1 py-3 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors flex items-center justify-center"
            >
              <SafeIcon icon={FiSave} className="mr-2" />
              Save
            </button>
            <button
              onClick={onClose}
              className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AdvancedSettingsPanel;
